import styled from 'styled-components';

export const colors = {
  primary: '#5768FF',
  primaryDark: '#4958df',
  primaryBackground: '#f0f2ff',
  secondary: '#f59e0b',
  success: '#10b981',
  danger: '#ef4444',
  gray: '#858585',
  lightGray: '#EFEFEF',
  white: '#ffffff',
  black: '#101010',
  chatBlue: '#5768FF',
  chatBg: '#f9fafb',
  bgGray: '#F9F9FB',
  borderGray: '#D6D6D6',
  green: '#62cf7a',
  greenBackground: '#f7fff0',
  red: '#d81e5b',
  redBackground: '#ffeaf1',
  grayTwo: '#666',
  grayThree: '#f0f0f0',
  yellow: '#f7dc6f',
  yellowBackground: '#fefce8',
  questionNumberBadge: '#F5F5F5',
  questionNumberBadgeText: '#636363',
  userIconDecision: '#BAC1FF',
};

type ContainerContentProps = {
  isTest?: boolean;
};

export const Container = styled.div<ContainerContentProps>`
  width: 100%;
  margin: ${(props) => (props.isTest ? '0' : '24px auto')};
  padding: ${(props) => (props.isTest ? '24px 0' : '0 24px')};
  gap: 22px;
`;

export const Content = styled.div<ContainerContentProps>`
  width: 100%;
  min-height: ${(props) => (props.isTest ? 'calc(100vh - 450px)' : 'calc(100vh - 220px)')};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background: ${colors.white};
  box-shadow: ${(props) => (props.isTest ? 'none' : '0px 2px 15px 0px rgba(0, 0, 0, 0.05)')};
  padding: ${(props) => (props.isTest ? '0' : '24px 0')};
  border-radius: ${(props) => (props.isTest ? '0' : '10px')};
  margin-top: ${(props) => (props.isTest ? '0' : '20px')};
`;

type RowProps = {
  alignItems?: string;
  justifyContent?: string;
  padding?: string;
  margin?: string;
  gap?: string;
  width?: string;
  maxWidthTransformCol?: string;
};

export const Row = styled.div<RowProps>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  flex-direction: row;
  justify-content: ${(props) => props.justifyContent || 'space-between'};
  align-items: ${(props) => props.alignItems || 'center'};
  padding: ${(props) => props.padding || '0'};
  margin: ${(props) => props.margin || '0'};
  gap: ${(props) => props.gap || '0'};
  width: ${(props) => props.width || 'auto'};

  @media screen and (max-width: ${(props) => props.maxWidthTransformCol || '768px'}) {
    width: 100%;
    flex-direction: column;
    gap: 12px;
  }
`;

type ColProps = {
  alignItems?: string;
  justifyContent?: string;
  padding?: string;
  height?: string;
  gap?: string;
  margin?: string;
  width?: string;
};

export const Col = styled.div<ColProps>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  align-items: ${(props) => props.alignItems || 'flex-start'};
  padding: ${(props) => props.padding || '0'};
  height: ${(props) => props.height || 'auto'};
  gap: ${(props) => props.gap || '0'};
  margin: ${(props) => props.margin || '0'};
  width: ${(props) => props.width || 'auto'};
`;

export const QuestionStyle = styled.div`
  margin-bottom: 14px;
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 24px;
  letter-spacing: 3%;
  color: ${colors.black};
  white-space: pre-wrap;

  p {
    margin: 0 0 16px;
  }

  ul,
  ol {
    margin: 16px 0;
    padding-left: 24px;
    list-style-position: outside;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin-bottom: 8px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  li > p {
    margin: 0;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 12px 0;
  }

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`;

type ButtonStyle = {
  styles: 'primary' | 'secondary';
  minWidth?: string;
  marginImage?: string;
  margin?: string;
  maxWidthTransformCol?: string;
};

export const Button = styled.button<ButtonStyle>`
  min-width: ${(props) => props.minWidth || '210px'};
  width: fit-content;
  height: 36px;
  gap: 4px;
  angle: 0deg;
  opacity: 1;
  padding: 14px;
  background: ${({ styles }) => (styles === 'primary' ? colors.primary : colors.white)};
  color: ${({ styles }) => (styles === 'primary' ? colors.white : colors.black)};
  border: ${({ styles }) => (styles === 'primary' ? 'none' : '1px solid ' + colors.borderGray)};

  border-radius: 8px;
  margin: ${(props) => props.margin || 'auto'};

  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: 3%;

  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    margin: ${(props) => props.marginImage || '0'};
  }

  &:hover:not(:disabled) {
    background: ${({ styles }) => (styles === 'primary' ? colors.primaryDark : colors.white)};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${colors.primary}40;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media screen and (max-width: ${(props) => props.maxWidthTransformCol || '768px'}) {
    min-width: 100%;
    margin: 0;
  }
`;

type FooterProps = {
  borderTop?: string;
};

export const Footer = styled.div<FooterProps>`
  width: 100%;
  border-top: ${(props) => props.borderTop || '1px solid #efefef'};
  margin: 0;
  padding: 16px 24px 0 16px;
  margin-top: auto;
`;

export const QuestionNumberBadge = styled.div`
  width: 69px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 1;
  border-radius: 100px;
  background: ${colors.questionNumberBadge};

  > p {
    font-family: Manrope, sans-serif;
    font-weight: 500;
    font-style: Medium;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 20px;
    letter-spacing: 3%;
    color: ${colors.questionNumberBadgeText};
  }
`;

export const Title = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;

  p {
    font-family: Manrope, sans-serif;
    font-weight: 500;
    font-style: Medium;
    font-size: 16px;
    leading-trim: NONE;
    line-height: 24px;
    letter-spacing: 3%;
    vertical-align: middle;
    color: ${colors.gray};
  }
`;
